// frontend/src/pages/EventPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import apiService from "../services/apiService";
import LoadingSpinner from "../components/common/LoadingSpinner";
import VideoPlayer from "../components/video/VideoPlayer";
import EventTypeBadge from "../components/event/EventTypeBadge";
import EventFormatBadge from "../components/event/EventFormatBadge";
import EventRegistrationForm from "../components/event/EventRegistrationForm";
import CalendarButton from "../components/event/CalendarButton";
import {
  Calendar,
  Clock,
  MapPin,
  Video,
  ExternalLink,
  Lock,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Users,
  Gift,
  Play,
} from "lucide-react";
import { getEventTimeDisplay } from "../utils/eventTimeUtils";

const EventPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { language, t } = useLanguage();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    fetchEvent();
  }, [slug, language]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getEvent(slug, language);

      if (response.success) {
        setEvent(response.data);
      } else {
        setError(response.message || "Event not found");
      }
    } catch (err) {
      console.error("Error fetching event:", err);
      setError("Failed to load event. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (participationType) => {
    if (!isAuthenticated) {
      navigate(`/login?returnUrl=/event/${slug}`);
      return;
    }

    setRegistering(true);
    setNotification(null);

    try {
      const response = await apiService.registerForEvent(slug, participationType);

      if (response.success) {
        setNotification({
          type: "success",
          message: response.message || t({ de: "Erfolgreich angemeldet!", en: "Successfully registered!" }),
        });
        fetchEvent(); // Reload to get updated participant info
      } else {
        setNotification({
          type: "error",
          message: response.message || t({ de: "Anmeldung fehlgeschlagen", en: "Registration failed" }),
        });
      }
    } catch (err) {
      console.error("Error registering:", err);
      setNotification({
        type: "error",
        message: t({ de: "Anmeldung fehlgeschlagen", en: "Registration failed" }),
      });
    } finally {
      setRegistering(false);
    }
  };

  const handleCancelRegistration = async () => {
    if (!window.confirm(t({ de: "Möchtest du deine Anmeldung wirklich stornieren?", en: "Are you sure you want to cancel your registration?" }))) {
      return;
    }

    setCancelling(true);
    setNotification(null);

    try {
      const response = await apiService.cancelEventRegistration(slug);

      if (response.success) {
        setNotification({
          type: "success",
          message: t({ de: "Anmeldung storniert", en: "Registration cancelled" }),
        });
        fetchEvent();
      } else {
        setNotification({
          type: "error",
          message: response.message || t({ de: "Stornierung fehlgeschlagen", en: "Cancellation failed" }),
        });
      }
    } catch (err) {
      console.error("Error cancelling:", err);
      setNotification({
        type: "error",
        message: t({ de: "Stornierung fehlgeschlagen", en: "Cancellation failed" }),
      });
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(language === "de" ? "de-DE" : "en-US", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: event?.timezone || "Europe/Zurich",
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t({ de: "Event nicht gefunden", en: "Event not found" })}
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link
            to="/events"
            className="inline-flex items-center gap-2 text-[#00A3E0] hover:underline"
          >
            <ArrowLeft className="w-5 h-5" />
            {t({ de: "Zurück zu Events", en: "Back to Events" })}
          </Link>
        </div>
      </div>
    );
  }

  const isUpcoming = new Date(event.start_datetime) > new Date();
  const isRegistered = event.participant?.status === "confirmed";
  const isInvited =
    event.participant?.source === "invitation" &&
    event.participant?.status === "pending";
  const isPast = new Date(event.end_datetime) < new Date();

  // Contract badge colors
  const contractColors = {
    Free: "bg-green-500",
    Registered: "bg-blue-500",
    Basic: "bg-[#23CEE7]",
    Premium: "bg-[#FC0058]",
    Pro: "bg-black",
    Invite: "bg-purple-600",
  };

  const timeDisplay = getEventTimeDisplay(event, language);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-4 pt-4">
        <Link
          to="/events"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-[#002855]"
        >
          <ArrowLeft className="w-5 h-5" />
          {t({ de: "Alle Events", en: "All Events" })}
        </Link>
      </div>

      {/* Hero Image */}
      <div className="max-w-4xl mx-auto px-4 mt-4">
        <div className="relative aspect-video bg-gradient-to-br from-[#002855] to-[#00A3E0] rounded-xl overflow-hidden">
          {event.imageUrl && (
            <img
              src={event.imageUrl}
              alt={event.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )}

          {/* Badges */}
          <div className="absolute top-4 left-4 flex gap-2">
            <EventFormatBadge format={event.format} />
            <EventTypeBadge type={event.event_type} />
          </div>

          <div
            className={`absolute top-4 right-4 ${
              contractColors[event.access_type] || "bg-gray-500"
            } text-white text-sm font-bold px-3 py-1 rounded`}
          >
            {event.access_type}
          </div>

          {/* Status indicator */}
          {isPast && (
            <div className="absolute bottom-4 right-4 bg-gray-800 text-white px-3 py-1 rounded-md">
              {t({ de: "Beendet", en: "Ended" })}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <h1 className="text-3xl font-bold text-[#002855]">{event.title}</h1>

            {/* Abstract */}
            {event.abstract && (
              <p className="text-lg text-gray-600">{event.abstract}</p>
            )}

            {/* Trailer Video */}
            {event.trailer && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Play className="w-5 h-5" />
                  {t({ de: "Vorschau", en: "Preview" })}
                </h3>
                {event.trailer.videoUrl ? (
                  <div className="rounded-lg overflow-hidden">
                    <VideoPlayer
                      videoUrl={event.trailer.videoUrl}
                      posterUrl={event.trailer.thumbnailUrl}
                      title={t(event.trailer.title)}
                      subtitles={event.trailer.subtitles}
                      videoLanguage={event.trailer.language}
                    />
                  </div>
                ) : (
                  <Link
                    to={`/video/${event.trailer.slug}`}
                    className="text-[#00A3E0] hover:underline"
                  >
                    {t(event.trailer.title)}
                  </Link>
                )}
              </div>
            )}

            {/* Description */}
            {event.description && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold text-gray-800 mb-3">
                  {t({ de: "Beschreibung", en: "Description" })}
                </h3>
                <div
                  className="prose prose-sm max-w-none text-gray-600"
                  dangerouslySetInnerHTML={{ __html: event.description }}
                />
              </div>
            )}

            {/* Recording (for past events) */}
            {isPast && event.recording && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  {t({ de: "Aufzeichnung", en: "Recording" })}
                </h3>
                {event.recording.type === "video" && (
                  <Link
                    to={`/video/${event.recording.slug}`}
                    className="text-[#00A3E0] hover:underline"
                  >
                    {event.recording.title}
                  </Link>
                )}
                {event.recording.type === "playlist" && (
                  <Link
                    to={`/playlist/${event.recording.slug}`}
                    className="text-[#00A3E0] hover:underline"
                  >
                    {event.recording.title}
                  </Link>
                )}
                {event.recording.type === "external" && (
                  <a
                    href={event.recording.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#00A3E0] hover:underline"
                  >
                    {t({ de: "Aufzeichnung ansehen", en: "Watch Recording" })}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Event Details Card */}
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              {/* Date */}
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-[#EE005F] mt-0.5" />
                <div>
                  <div className="font-medium text-gray-800">
                    {formatDate(event.start_datetime)}
                  </div>
                </div>
              </div>

              {/* Time */}
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#EE005F] mt-0.5" />
                <div>
                  <div className="font-medium text-gray-800">
                    {timeDisplay.eventTimeLine.start} -{" "}
                    {timeDisplay.eventTimeLine.end}{" "}
                    <span className="text-sm font-normal text-gray-500">
                      {timeDisplay.eventTimeLine.abbreviation}
                    </span>
                  </div>
                  {timeDisplay.showLocalTime && timeDisplay.localTimeLine && (
                    <div className="text-sm text-gray-500">
                      {timeDisplay.localTimeLine.start} -{" "}
                      {timeDisplay.localTimeLine.end}{" "}
                      {timeDisplay.localTimeLine.abbreviation}{" "}
                      <span className="text-xs">
                        ({timeDisplay.localTimeLine.label})
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Location (for in-person/hybrid) */}
              {event.location && event.format !== "online" && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#EE005F] mt-0.5" />
                  <div className="font-medium text-gray-800">
                    {event.location}
                  </div>
                </div>
              )}

              {/* Capacity */}
              {(event.capacity?.max_online || event.capacity?.max_in_person) && (
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-[#EE005F] mt-0.5" />
                  <div className="text-sm text-gray-600">
                    {event.format === "hybrid" ? (
                      <>
                        {event.capacity.spotsRemaining?.online !== null && (
                          <div>
                            {event.capacity.spotsRemaining.online} /{" "}
                            {event.capacity.max_online}{" "}
                            {t({ de: "Online-Plätze", en: "online spots" })}
                          </div>
                        )}
                        {event.capacity.spotsRemaining?.in_person !== null && (
                          <div>
                            {event.capacity.spotsRemaining.in_person} /{" "}
                            {event.capacity.max_in_person}{" "}
                            {t({ de: "Vor-Ort-Plätze", en: "in-person spots" })}
                          </div>
                        )}
                      </>
                    ) : (
                      <div>
                        {event.capacity.spotsRemaining?.online !== null
                          ? `${event.capacity.spotsRemaining.online} / ${event.capacity.max_online}`
                          : `${event.capacity.spotsRemaining?.in_person} / ${event.capacity.max_in_person}`}{" "}
                        {t({ de: "Plätze verfügbar", en: "spots available" })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pricing */}
              {event.pricing_type === "donation" && event.suggested_donation && (
                <div className="flex items-start gap-3">
                  <Gift className="w-5 h-5 text-[#EE005F] mt-0.5" />
                  <div>
                    <div className="font-medium text-gray-800">
                      {t({ de: "Kollekte", en: "Donation" })}
                    </div>
                    <div className="text-sm text-gray-500">
                      {event.donation_currency} {event.suggested_donation}{" "}
                      {t({ de: "empfohlen", en: "suggested" })}
                    </div>
                  </div>
                </div>
              )}

              {event.pricing_type === "external" && event.external_registration_url && (
                <div className="border-t pt-4 mt-4">
                  <a
                    href={event.external_registration_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#00A3E0] hover:underline"
                  >
                    {t({ de: "Externe Anmeldung", en: "External Registration" })}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              )}
            </div>

            {/* Notification */}
            {notification && (
              <div
                className={`rounded-lg p-4 ${
                  notification.type === "success"
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                <div className="flex items-center gap-2">
                  {notification.type === "success" ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <XCircle className="w-5 h-5" />
                  )}
                  {notification.message}
                </div>
              </div>
            )}

            {/* Registration Section */}
            {isUpcoming && !isPast && (
              <div className="bg-white rounded-lg shadow p-6">
                {isRegistered ? (
                  // Already registered
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-green-600 font-semibold">
                      <CheckCircle className="w-5 h-5" />
                      {t({ de: "Du bist angemeldet!", en: "You're registered!" })}
                    </div>

                    {event.participant?.participationType && (
                      <div className="text-sm text-gray-600">
                        {t({ de: "Teilnahme:", en: "Participation:" })}{" "}
                        {event.participant.participationType === "online"
                          ? t({ de: "Online", en: "Online" })
                          : t({ de: "Vor Ort", en: "In-Person" })}
                      </div>
                    )}

                    {/* Meeting Link */}
                    {event.meeting_url && (
                      <a
                        href={event.meeting_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-[#00A3E0] text-white px-4 py-3 rounded-lg font-semibold hover:bg-[#0090c5] transition-colors"
                      >
                        <Video className="w-5 h-5" />
                        {t({ de: "Event beitreten", en: "Join Event" })}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}

                    {/* Calendar Download */}
                    <CalendarButton eventSlug={event.slug} className="w-full" />

                    {/* Cancel Registration */}
                    <button
                      onClick={handleCancelRegistration}
                      disabled={cancelling}
                      className="w-full text-red-600 hover:text-red-700 text-sm py-2"
                    >
                      {cancelling
                        ? t({ de: "Storniere...", en: "Cancelling..." })
                        : t({ de: "Anmeldung stornieren", en: "Cancel Registration" })}
                    </button>
                  </div>
                ) : !event.hasAccess ? (
                  // No access
                  <div className="text-center space-y-4">
                    <Lock className="w-12 h-12 text-gray-300 mx-auto" />
                    <p className="text-gray-600">
                      {event.accessReason === "login_required" &&
                      event.access_type === "Invite"
                        ? t({
                            de: "Dieses Event ist nur auf Einladung. Melde dich an, um zu prüfen, ob du eine Einladung hast.",
                            en: "This event is invite-only. Log in to check if you have an invitation.",
                          })
                        : event.accessReason === "login_required"
                        ? t({
                            de: "Bitte melde dich an, um dich zu registrieren",
                            en: "Please log in to register",
                          })
                        : event.accessReason === "invite_required"
                        ? t({
                            de: "Dieses Event ist nur auf Einladung",
                            en: "This event is invite-only",
                          })
                        : t({
                            de: `${event.access_type}-Abo erforderlich`,
                            en: `${event.access_type} subscription required`,
                          })}
                    </p>
                    {event.accessReason === "login_required" && (
                      <Link
                        to={`/login?returnUrl=/event/${event.slug}`}
                        className="inline-block bg-[#EE005F] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#cc0050] transition-colors"
                      >
                        {t({ de: "Anmelden", en: "Log In" })}
                      </Link>
                    )}
                  </div>
                ) : isInvited ? (
                  // Invited but not confirmed
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-purple-600 font-semibold">
                      {t({ de: "Du bist eingeladen!", en: "You're invited!" })}
                    </div>
                    <EventRegistrationForm
                      event={event}
                      onRegister={handleRegister}
                      loading={registering}
                    />
                  </div>
                ) : (
                  // Can register
                  <EventRegistrationForm
                    event={event}
                    onRegister={handleRegister}
                    loading={registering}
                  />
                )}
              </div>
            )}

            {/* Past event notice */}
            {isPast && (
              <div className="bg-gray-100 rounded-lg p-6 text-center text-gray-600">
                {t({
                  de: "Dieses Event ist bereits beendet",
                  en: "This event has ended",
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventPage;
